/**
 * Estimate panel cards for image-level scale and measuring (web/shared/src/measure.ts):
 * the selected drawing image (set scale by calibrating or from a printed-sheet
 * preset, lock / unlock, remove), and a selected plain or measure line / shape
 * (its measured length or area, "Measure this line", count it as a run).
 * Canvas work (two-click calibration, writing customData) is the board app's.
 */
import { useState } from 'react';
import { SCALE_PRESETS, type MeasureDisplay } from '../measure';

export interface SelectedImage {
  elementId: string;
  /** "1/8\" = 1'-0\"", or null when the image has no scale yet. */
  scaleLabel: string | null;
  locked: boolean;
  /** Printed sheet size is known (presets available). */
  hasSheetSize: boolean;
  sourceLabel: string | null;
}

export interface SelectedMeasure {
  elementId: string;
  kind: 'linear' | 'area';
  /** "24'-6\"" / "320 sf", or null when no scaled drawing is under it. */
  text: string | null;
  /** Name of the drawing it was measured on. */
  on: string | null;
  /** Crosses two scaled drawings. */
  spans: boolean;
  /** Already tagged as a measurement (vs a plain line). */
  isMeasure: boolean;
}

export function ImageScaleCard(p: {
  image: SelectedImage;
  readOnly: boolean;
  onCalibrate(): void;
  onPreset(presetKey: string): void;
  onToggleLock(): void;
  onRemoveScale(): void;
}) {
  const { image } = p;
  const [preset, setPreset] = useState('');
  return (
    <div className="rl-est-card" data-testid="estimate-image">
      <div className="rl-est-card-h">
        <strong>Drawing</strong>
        <span className="rl-muted">{image.sourceLabel ?? 'image'}</span>
        <span className="rl-spacer" />
        {image.locked ? <span className="rl-chip" title="Locked so it can't move while you measure">🔒 locked</span> : null}
      </div>
      <p className="rl-est-hint" data-testid="estimate-image-scale">
        {image.scaleLabel ? <>📏 {image.scaleLabel}</> : <span className="rl-muted">No scale yet — runs and areas drawn on it can't be measured.</span>}
      </p>
      {!p.readOnly ? (
        <>
          <div className="rl-est-grid2">
            <button className="rl-btn" data-variant={image.scaleLabel ? undefined : 'primary'} onClick={p.onCalibrate} data-testid="estimate-image-calibrate" title="Click two points on the drawing and type the real distance (⇧C)">
              {image.scaleLabel ? 'Recalibrate…' : 'Set scale…'}
            </button>
            {image.hasSheetSize ? (
              <select
                className="rl-select"
                value={preset}
                aria-label="Printed scale"
                data-testid="estimate-image-preset"
                onChange={(ev) => {
                  setPreset('');
                  if (ev.target.value) p.onPreset(ev.target.value);
                }}
              >
                <option value="">Printed scale…</option>
                {(['Architectural', 'Engineering', 'Metric'] as const).map((g) => (
                  <optgroup key={g} label={g}>
                    {SCALE_PRESETS.filter((s) => s.group === g).map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              <span className="rl-muted rl-est-hint" title="Presets need the printed sheet size. Drop the PDF onto the board, or calibrate.">
                Presets need the sheet size
              </span>
            )}
          </div>
          <div className="rl-est-grid2">
            <button className="rl-btn" data-size="sm" onClick={p.onToggleLock} data-testid="estimate-image-lock">
              {image.locked ? 'Unlock (keeps the scale)' : 'Lock'}
            </button>
            {image.scaleLabel ? (
              <button className="rl-btn" data-size="sm" onClick={p.onRemoveScale} data-testid="estimate-image-remove-scale">
                Remove scale
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Measured length / area of a selected line or shape, with "Measure this line". */
export function MeasureLine({ m, readOnly, onMeasure }: { m: SelectedMeasure; readOnly: boolean; onMeasure(): void }) {
  return (
    <div className="rl-est-measure" data-testid="estimate-measure">
      {m.text ? (
        <p className="rl-est-hint">
          <strong data-testid="estimate-measure-value">{m.text}</strong>
          {m.on ? <span className="rl-muted"> on {m.on}</span> : null}
          {m.spans ? <span className="rl-est-warn"> — crosses two scaled drawings; measured on the one holding most of it</span> : null}
        </p>
      ) : (
        <p className="rl-muted rl-est-hint">No scaled drawing under it — set a drawing's scale to measure.</p>
      )}
      {!m.isMeasure && !readOnly ? (
        <button className="rl-btn" data-size="sm" onClick={onMeasure} data-testid="estimate-measure-this">
          Measure this {m.kind === 'area' ? 'shape' : 'line'}
        </button>
      ) : null}
    </div>
  );
}

export const MEASURE_DISPLAY_OPTIONS: { value: MeasureDisplay; label: string }[] = [
  { value: 'ftin', label: `Feet and inches (24'-6")` },
  { value: 'ft', label: 'Decimal feet (24.5 ft)' },
  { value: 'm', label: 'Metres (7.47 m)' },
];
